<template>
  <div class="profile-view-container pa-4 position-relative">
    <v-btn
      icon="mdi-arrow-left"
      variant="text"
      @click="handleBackClick"
      class="d-none d-md-flex position-absolute"
      style="left: -48px; top: 16px;"
    ></v-btn>

    <!-- Profile Header -->
    <div 
      v-show="!isCurrentlyEditing" 
      v-if="!isSingleNoteView" 
      class="position-relative mb-8 profile-header border border-white-05 overflow-hidden"
      :style="headerStyle"
      :class="{ 'header-top-rounded': true }"
    >
      <!-- Banner Overlay for readability if banner exists -->
      <div v-if="displayProfile?.banner" class="profile-banner-overlay"></div>

      <div class="pa-4 pa-sm-6 position-relative h-100" style="z-index: 1;">
        <v-btn 
          icon="mdi-arrow-left" 
          variant="text" 
          @click="$emit('back')" 
          class="position-absolute d-md-none" 
          style="top: 12px; left: 12px; z-index: 2"
        ></v-btn>

      <div class="position-absolute d-flex align-center" style="top: 16px; right: 16px; z-index: 10">
        <v-btn icon variant="text" color="grey-lighten-1">
          <v-icon icon="mdi-dots-vertical"></v-icon>
          <v-menu activator="parent" transition="slide-y-transition" location="bottom end">
            <v-list density="compact" class="rounded-lg shadow-lg bg-surface border border-white-05">
              <v-list-item
                v-if="isOwnProfile && user"
                prepend-icon="mdi-pencil-outline"
                title="Edit Profile"
                @click="showEditProfileDialog = true"
              ></v-list-item>
              <v-list-item
                v-if="isOwnProfile && user"
                prepend-icon="mdi-briefcase-edit-outline"
                title="Edit Portfolio"
                @click="showEditor = true"
              ></v-list-item>
              <v-divider v-if="isOwnProfile && user" class="my-1 border-white-05"></v-divider>
              <v-list-item
                prepend-icon="mdi-link-variant"
                title="Copy link to profile"
                @click="copyProfileLink"
              ></v-list-item>
              <v-list-item
                v-if="isOwnProfile"
                prepend-icon="mdi-export"
                title="Export Portfolio (Single HTML)"
                @click="handleExportPortfolio"
                :loading="exporting"
              ></v-list-item>
              <v-list-item
                v-if="user && !isOwnProfile"
                prepend-icon="mdi-playlist-plus"
                title="Add to lists"
                @click="showListAddDialog = true"
              ></v-list-item>
            </v-list>
          </v-menu>
        </v-btn>
      </div>

      <div class="d-flex flex-column align-center text-center mt-12 mt-md-8 pt-4">
        <v-avatar size="100" class="mb-4 border-2 border-primary shrink-0 elevation-4">
          <v-img v-if="displayProfile?.avatar" :src="displayProfile.avatar"></v-img>
          <v-icon v-else icon="mdi-account" size="64"></v-icon>
        </v-avatar>
        <div class="flex-grow-1" style="min-width: 0; width: 100%;">
          <h2 class="text-h5 text-sm-h4 font-weight-black mb-1 text-truncate">{{ displayProfile?.displayName || displayProfile?.handle }}</h2>
          <div class="text-subtitle-2 text-sm-subtitle-1 text-grey mb-3 text-truncate">@{{ displayProfile?.handle }}</div>
          <div 
            v-if="displayProfile?.description" 
            class="text-body-2 text-grey-lighten-1 mb-4" 
            style="word-break: break-word; white-space: pre-wrap;" 
            v-html="renderContent(displayProfile.description)"
            @click="handleContentClick"
          ></div>
          
          <div class="mb-4 d-flex justify-center gap-6">
            <v-btn
              v-if="!isOwnProfile && user"
              :loading="followLoading"
              :color="isFollowedByMe ? 'grey-darken-3' : 'primary'"
              :variant="isFollowedByMe ? 'flat' : 'elevated'"
              class="rounded-pill px-6"
              @click="isFollowedByMe ? handleUnfollow() : handleFollow()"
            >
              <v-icon :icon="isFollowedByMe ? 'mdi-account-minus' : 'mdi-account-plus'" class="mr-2"></v-icon>
              {{ isFollowedByMe ? 'Unfollow' : 'Follow' }}
            </v-btn>
            <v-btn
              v-if="!isOwnProfile && user"
              variant="outlined"
              class="rounded-pill px-6"
              @click="$emit('message', displayProfile)"
            >
              <v-icon icon="mdi-message-outline" class="mr-2"></v-icon>
              Message
            </v-btn>

          </div>
          
          <div class="d-flex justify-center gap-4 flex-wrap">
            <div class="stat-item d-flex flex-column align-center">
              <span class="text-h6 font-weight-bold">{{ displayProfile?.postsCount || 0 }}</span>
              <span class="text-caption text-grey text-uppercase">Posts</span>
            </div>
            <v-divider vertical class="mx-2 mx-sm-4 my-2"></v-divider>
            <div class="stat-item d-flex flex-column align-center cursor-pointer hover-opacity" @click="openFollowers">
              <span class="text-h6 font-weight-bold">{{ displayProfile?.followersCount || 0 }}</span>
              <span class="text-caption text-grey text-uppercase">Followers</span>
            </div>
            <v-divider vertical class="mx-2 mx-sm-4 my-2"></v-divider>
            <div class="stat-item d-flex flex-column align-center cursor-pointer hover-opacity" @click="openFollowing">
              <span class="text-h6 font-weight-bold">{{ displayProfile?.followsCount || 0 }}</span>
              <span class="text-caption text-grey text-uppercase">Following</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

    <!-- Tabs -->
    <div v-show="!isCurrentlyEditing && !isSingleNoteView">
      <v-tabs
        v-model="activeTab"
        color="primary"
      grow
      class="mb-6 rounded-lg bg-black-05 overflow-hidden"
    >
      <v-tab value="about">
        <v-icon icon="mdi-account-details" class="mr-2"></v-icon>
        About Me
      </v-tab>
      <v-tab value="posts">
        <v-icon icon="mdi-post" class="mr-2"></v-icon>
        {{ isOwnProfile ? 'My Posts' : 'Posts' }}
      </v-tab>
      <v-tab value="bookmarks">
        <v-icon icon="mdi-bookmark-multiple" class="mr-2"></v-icon>
        {{ isOwnProfile ? 'My Bookmarks' : 'Bookmarks' }}
      </v-tab>
      <v-tab value="notes">
        <v-icon icon="mdi-note-text-outline" class="mr-2"></v-icon>
        {{ isOwnProfile ? 'My Stories' : 'Stories' }}
      </v-tab>
      <v-tab value="contact">
        <v-icon icon="mdi-at" class="mr-2"></v-icon>
        Contact
      </v-tab>
      <v-tab v-if="isStudioMember" value="jobs">
        <v-icon icon="mdi-briefcase" class="mr-2"></v-icon>
        Jobs
      </v-tab>
    </v-tabs>
    </div>

    <v-window v-model="activeTab" :class="{ 'overflow-visible': isCurrentlyEditing }">
      <!-- About Me Tab -->
      <v-window-item value="about">
        <div class="profile-tab-wrapper" v-if="activeTab === 'about'">
          <PortfolioView 
            ref="portfolioViewRef"
            :user-did="displayProfile?.did || (userDid?.startsWith('did:') ? userDid : null)" 
            :is-own-profile="isOwnProfile"
            @edit="showEditor = true"
          />
        </div>
      </v-window-item>
 
      <!-- Posts Tab -->
      <v-window-item value="posts">
        <div class="profile-tab-wrapper" v-if="activeTab === 'posts'">
          <div v-if="loadingPosts" class="text-center py-10">
            <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
            <p class="mt-4 text-grey">Retrieving your gamedev history...</p>
          </div>
 
          <div v-else-if="posts.length > 0">
            <v-fade-transition group>
              <PostItem
                v-for="item in posts"
                :key="item.post?.uri || item.uri"
                :post="item.post || item"
                :reply="item.reply"
                @view-thread="$emit('view-thread', item.post || item)"
                @delete="onPostDeleted"
                @tag-click="$emit('tag-click', $event)"
              />
            </v-fade-transition>
            
            <!-- Load More Button -->
            <div v-if="postsCursor != null && postsCursor !== '' && !loadingPosts" class="d-flex justify-center py-10">
              <v-btn
                color="primary"
                variant="tonal"
                rounded="pill"
                size="large"
                class="px-10 font-weight-black"
                :loading="loadingMorePosts"
                @click="loadMorePosts"
              >
                <v-icon start icon="mdi-chevron-down"></v-icon>
                Load More Posts
              </v-btn>
            </div>
          </div>
 
          <div v-else class="text-center py-12 border rounded-xl border-dashed border-white-05">
            <v-icon icon="mdi-text-box-outline" size="64" color="grey-darken-2" class="mb-4"></v-icon>
            <h3 class="text-h5 text-grey">You haven't posted anything yet</h3>
            <p class="text-body-2 text-grey-darken-1 mt-2">Share your progress with #gamedevid!</p>
          </div>
        </div>
      </v-window-item>
 
      <!-- Bookmarks Tab -->
      <v-window-item value="bookmarks">
        <div class="profile-tab-wrapper" v-if="activeTab === 'bookmarks'">
          <BookmarkView 
            :user-did="displayProfile?.did || (userDid?.startsWith('did:') ? userDid : null)"
            @view-thread="$emit('view-thread', $event)"
            @delete="onPostDeleted"
            @tag-click="$emit('tag-click', $event)"
          />
        </div>
      </v-window-item>
 
      <!-- Notes Tab -->
      <v-window-item value="notes">
        <div class="profile-tab-wrapper" :style="isCurrentlyEditing ? 'overflow: visible !important;' : ''" v-if="activeTab === 'notes'">
          <NotesView 
            ref="notesViewRef"
            :handle="displayProfile?.handle || props.handle"
            :is-own-profile="isOwnProfile"
            @share-note="$emit('share-note', $event)"
            @activate-discussion="$emit('activate-discussion', $event)"
            @login="$emit('login')"
          />
        </div>
      </v-window-item>

      <!-- Contact Tab -->
      <v-window-item value="contact">
        <div class="profile-tab-wrapper" v-if="activeTab === 'contact'">
          <ContactView
            :user-did="displayProfile?.did || (userDid?.startsWith('did:') ? userDid : null)"
            :is-own-profile="isOwnProfile"
          />
        </div>
      </v-window-item>

      <!-- Jobs Tab -->
      <v-window-item v-if="isStudioMember" value="jobs">
        <div class="profile-tab-wrapper" v-if="activeTab === 'jobs'">
          <div v-if="loadingJobs" class="text-center py-10">
            <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
          </div>
          <div v-else-if="jobPosts.length === 0" class="text-center py-12 border rounded-xl border-dashed border-white-05">
            <v-icon icon="mdi-briefcase-outline" size="64" color="grey-darken-2" class="mb-4"></v-icon>
            <h3 class="text-h5 text-grey">No job vacancies posted</h3>
            <p class="text-body-2 text-grey-darken-1 mt-2">Posts tagged #gdijob will appear here.</p>
          </div>
          <div v-else>
            <PostItem
              v-for="post in jobPosts"
              :key="post.uri"
              :post="post"
              @view-thread="$emit('view-thread', post)"
              @delete="onPostDeleted"
              @tag-click="$emit('tag-click', $event)"
            />
          </div>
        </div>
      </v-window-item>
    </v-window>

    <!-- Portfolio Editor Dialog -->
    <v-dialog v-model="showEditor" max-width="600px" persistent>
      <PortfolioEditor @close="showEditor = false" @saved="onPortfolioSaved" />
    </v-dialog>
    <v-snackbar v-model="snackbar" :timeout="2000" color="success" rounded="pill" class="mb-4">
      {{ snackbarText }}
    </v-snackbar>

    <ListAddDialog
      v-if="displayProfile"
      v-model="showListAddDialog"
      :actor-did="displayProfile.did"
      :actor-handle="displayProfile.handle"
      :actor-avatar="displayProfile.avatar"
    />

    <v-dialog v-model="showEditProfileDialog" max-width="600px" persistent>
      <EditProfileDialog @close="showEditProfileDialog = false" @saved="onProfileUpdated" />
    </v-dialog>

    <UserListDialog
      v-model="showUserList"
      :type="userListType"
      :actor="displayProfile?.did"
    />

    <!-- Export Portfolio Modal -->
    <v-dialog v-model="showExportModal" max-width="600" persistent>
      <v-card class="rounded-xl pa-4 bg-surface border border-white-05">
        <v-card-title class="text-h5 font-weight-black d-flex align-center">
          <v-icon icon="mdi-export-variant" class="mr-2" color="primary"></v-icon>
          Export Your Portfolio
        </v-card-title>
        
        <v-card-text>
          <v-alert
            color="primary"
            variant="tonal"
            icon="mdi-information-outline"
            class="mb-6 rounded-lg text-body-2"
          >
            This will generate a self-contained <strong>.html</strong> file. You can host this file for free on services like 
            <strong>GitHub Pages</strong>, <strong>Netlify</strong>, or <strong>Vercel</strong> to share your portfolio with anyone.
          </v-alert>

          <div class="text-subtitle-1 font-weight-bold mb-3">Choose a Theme</div>
          <v-row dense>
            <v-col v-for="t in themes" :key="t.id" cols="6" sm="3">
              <v-btn
                block
                height="100"
                :variant="selectedTheme === t.id ? 'flat' : 'outlined'"
                :color="selectedTheme === t.id ? 'primary' : 'grey-darken-2'"
                class="rounded-xl border-sm"
                :class="{ 'border-primary': selectedTheme === t.id }"
                @click="selectedTheme = t.id"
              >
                <div class="d-flex flex-column align-center w-100">
                  <div 
                    class="rounded-circle mb-2 shadow-sm" 
                    :style="{ background: t.preview, width: '32px', height: '32px', border: '2px solid rgba(255,255,255,0.2)' }"
                  ></div>
                  <span class="text-caption font-weight-black">{{ t.name }}</span>
                </div>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn variant="text" class="rounded-pill px-6" @click="showExportModal = false">Cancel</v-btn>
          <v-btn 
            color="primary" 
            variant="elevated" 
            class="rounded-pill px-8 font-weight-black" 
            :loading="exporting"
            @click="confirmExport"
          >
            Export Now
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  userProfile as globalUserProfile,
  fetchUserProfile,
  getAuthorFeed,
  getCurrentUser,
  deletePost,
  agent,
  publicAgent,
  followUser,
  unfollowUser,
  lastDeletedPostUri,
  getListMembers,
  searchPosts
} from '@/services/bluesky';
import PostItem from '@/components/PostItem.vue';
import BookmarkView from './BookmarkView.vue';
import PortfolioView from '@/components/PortfolioView.vue';
import PortfolioEditor from '@/components/PortfolioEditor.vue';
import NotesView from './NotesView.vue';
import ContactView from './ContactView.vue';
import ListAddDialog from '@/components/ListAddDialog.vue';
import UserListDialog from '@/components/UserListDialog.vue';
import EditProfileDialog from '@/components/EditProfileDialog.vue';
import { renderRichText, handleRichTextClick } from '@/utils/richtext';
import { 
  aggregateExportData, 
  generatePortfolioHtml, 
  downloadPortfolio,
  type ExportTheme 
} from '@/services/exporter';

const props = defineProps<{
  handle?: string;
}>();

const userDid = computed(() => props.handle);

const emit = defineEmits(['back', 'view-thread', 'tag-click', 'delete', 'view-profile', 'share-note', 'message', 'activate-discussion', 'login']);

const route = useRoute();
const router = useRouter();
const activeTab = ref((route.query.tab as string) || 'about');
const showEditor = ref(false);
const portfolioViewRef = ref<any>(null);
const notesViewRef = ref<any>(null);
const posts = ref<any[]>([]);
const loadingPosts = ref(false);
const postsCursor = ref<string | undefined>(undefined);
const loadingMorePosts = ref(false);
const localProfile = ref<any>(null);
const showEditProfileDialog = ref(false);
const showUserList = ref(false);
const exporting = ref(false);
const showExportModal = ref(false);
const selectedTheme = ref<ExportTheme>('gold');
const themes: { id: ExportTheme; name: string; preview: string }[] = [
  { id: 'white', name: 'White', preview: '#ffffff' },
  { id: 'gold', name: 'Golden', preview: '#DBA858' },
  { id: 'blue', name: 'Blue', preview: '#0c4a6e' },
  { id: 'grey', name: 'Grey', preview: '#171717' },
];
const userListType = ref<'likes' | 'reposts' | 'quotes' | 'followers' | 'follows'>('followers');

const openFollowers = () => {
  if (!displayProfile.value?.did) return;
  userListType.value = 'followers';
  showUserList.value = true;
};

const openFollowing = () => {
  if (!displayProfile.value?.did) return;
  userListType.value = 'follows';
  showUserList.value = true;
};

const headerStyle = computed(() => {
  if (displayProfile.value?.banner) {
    return {
      backgroundImage: `url(${displayProfile.value.banner})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      minHeight: '260px'
    };
  }
  return { 
    background: 'rgba(0, 0, 0, 0.2)',
    minHeight: 'auto'
  };
});

const isCurrentlyEditing = computed(() => {
  return activeTab.value === 'notes' && notesViewRef.value?.editingNote;
});

const isSingleNoteView = computed(() => route.path.includes('/note/'));

watch(() => route.query.tab, (newTab) => {
  if (newTab && typeof newTab === 'string') {
    activeTab.value = newTab;
  }
});

watch(isSingleNoteView, (val) => {
  if (val) activeTab.value = 'notes';
}, { immediate: true });

watch([() => route.query.initialText, () => notesViewRef.value], ([text, notesRef]) => {
  if (text && notesRef && activeTab.value === 'notes') {
    // Small delay to ensure component is ready
    setTimeout(() => {
      notesRef.startNewNote(text as string);
      // Clean up URL
      router.replace({ path: route.path, query: { ...route.query, initialText: undefined } });
    }, 100);
  }
}, { immediate: true });

const handleBackClick = () => {
  if (isCurrentlyEditing.value) {
    notesViewRef.value.editingNote = false;
  } else if (isSingleNoteView.value) {
    const handle = displayProfile.value?.handle || userDid.value;
    router.push(`/profile/${handle}?tab=notes`);
  } else {
    emit('back');
  }
};

const isOwnProfile = computed(() => {
  const currentUser = user.value;
  if (!currentUser) return false;
  if (!userDid.value) return true;
  
  const targetActor = userDid.value.toLowerCase().replace('@', '');
  const myHandle = currentUser.handle.toLowerCase().replace('@', '');
  
  // 1. Check if the DID matches directly
  if (userDid.value === currentUser.did) return true;
  
  // 2. Check if the handle matches
  if (targetActor === myHandle) return true;
  
  // 3. Finally, once profile is loaded, check the resolved DID
  if (localProfile.value?.did === currentUser.did) return true;
  
  return false;
});

const isOwnBookmarks = computed(() => {
  const user = getCurrentUser();
  if (!userDid.value) return true;
  return !!(user && userDid.value === user.did);
});

const displayProfile = computed(() => {
  // If we have a local profile from the fetch, use it
  if (localProfile.value) return localProfile.value;
  // If we are viewing "my profile" (no userDid prop), use global user profile
  if (!userDid.value) return globalUserProfile.value;
  // Otherwise, we're loading someone else's profile, return null for now
  return null;
});

const user = computed(() => getCurrentUser());
const followLoading = ref(false);
const snackbar = ref(false);
const snackbarText = ref('');
const showListAddDialog = ref(false);

const isFollowedByMe = computed(() => !!displayProfile.value?.viewer?.following);

const copyProfileLink = () => {
  if (!displayProfile.value) return;
  const url = `${window.location.origin}/profile/${displayProfile.value.handle}`;
  navigator.clipboard.writeText(url).then(() => {
    snackbarText.value = 'Link copied to clipboard';
    snackbar.value = true;
  });
};

const handleExportPortfolio = async () => {
  if (!displayProfile.value) return;
  showExportModal.value = true;
};

const confirmExport = async () => {
  if (!displayProfile.value) return;
  exporting.value = true;
  try {
    const data = await aggregateExportData(displayProfile.value.did);
    const html = generatePortfolioHtml(data, selectedTheme.value);
    const filename = `${displayProfile.value.handle}-portfolio.html`;
    downloadPortfolio(html, filename);
    showExportModal.value = false;
    snackbarText.value = 'Portfolio exported successfully!';
    snackbar.value = true;
  } catch (err) {
    console.error('Export failed:', err);
    snackbarText.value = 'Export failed. Please try again.';
    snackbar.value = true;
  } finally {
    exporting.value = false;
  }
};
const followUri = computed(() => displayProfile.value?.viewer?.following);

// Studio / Jobs tab
const STUDIO_LIST_URI = import.meta.env.VITE_GAME_STUDIO_LIST_URI as string;
const isStudioMember = ref(false);
const jobPosts = ref<any[]>([]);
const loadingJobs = ref(false);
const jobsLoaded = ref(false);

const checkStudioMembership = async (handle: string) => {
  if (!STUDIO_LIST_URI || !handle) return;
  try {
    let cursor: string | undefined;
    outer: do {
      const { items, cursor: next } = await getListMembers(STUDIO_LIST_URI, 100, cursor);
      for (const item of items) {
        if ((item.subject as any).handle === handle) {
          isStudioMember.value = true;
          break outer;
        }
      }
      cursor = next;
    } while (cursor);
  } catch { /* silent */ }
};

const loadJobPosts = async () => {
  const handle = displayProfile.value?.handle;
  if (!handle || loadingJobs.value || jobsLoaded.value) return;
  loadingJobs.value = true;
  try {
    const { posts } = await searchPosts(`from:${handle} #gdijob`, 20);
    jobPosts.value = posts;
    jobsLoaded.value = true;
  } catch {
    jobPosts.value = [];
  } finally {
    loadingJobs.value = false;
  }
};

const loadProfile = async () => {
  localProfile.value = null;
  try {
    let actor = userDid.value || user.value?.did;
    if (!actor) return;
    
    // Sanitize actor: remove leading @ and trailing dots
    if (actor.startsWith('@')) actor = actor.slice(1);
    if (actor.endsWith('.')) {
      actor = actor.slice(0, -1);
    }
    
    // Try with main agent first
    try {
      const response = await agent.getProfile({ actor });
      localProfile.value = response.data;
    } catch (profileErr) {
      console.warn('Main agent profile fetch failed, trying publicAgent:', profileErr);
      try {
        const response = await publicAgent.getProfile({ actor });
        localProfile.value = response.data;
      } catch (fallbackErr) {
        console.error('Public agent also failed to load profile:', fallbackErr);
        throw profileErr; // Re-throw if fallback also fails
      }
    }
    
    // If it's the current user, refresh the global profile as well
    if (localProfile.value?.did === user.value?.did) {
      await fetchUserProfile();
    }

    // Check if this profile is a studio member
    isStudioMember.value = false;
    jobsLoaded.value = false;
    jobPosts.value = [];
    if (localProfile.value?.handle) {
      checkStudioMembership(localProfile.value.handle);
    }
  } catch (e) {
    console.error('Failed to load profile for', userDid.value, e);
  }
};

const loadUserPosts = async () => {
  const targetDid = userDid.value || user.value?.did;
  if (!targetDid) return;
  loadingPosts.value = true;
  postsCursor.value = undefined;
  posts.value = [];
  try {
    const res = await getAuthorFeed(targetDid);
    posts.value = res.feed;
    postsCursor.value = res.cursor;
  } catch (error) {
    console.error('Failed to load user posts:', error);
  } finally {
    loadingPosts.value = false;
  }
};

const loadMorePosts = async () => {
  const targetDid = userDid.value || user.value?.did;
  if (!targetDid || loadingMorePosts.value || !postsCursor.value) return;
  loadingMorePosts.value = true;
  try {
    const res = await getAuthorFeed(targetDid, 50, postsCursor.value);
    posts.value = [...posts.value, ...res.feed];
    postsCursor.value = res.cursor;
  } catch (error) {
    console.error('Failed to load more user posts:', error);
  } finally {
    loadingMorePosts.value = false;
  }
};

const handleFollow = async () => {
  if (!displayProfile.value?.did) return;
  followLoading.value = true;
  try {
    const result = await followUser(displayProfile.value.did);
    // Update local state by refetching profile to get new viewer state
    await loadProfile();
  } catch (err) {
    console.error('Follow failed:', err);
  } finally {
    followLoading.value = false;
  }
};

const handleUnfollow = async () => {
  if (!followUri.value) return;
  followLoading.value = true;
  try {
    await unfollowUser(followUri.value);
    await loadProfile();
  } catch (err) {
    console.error('Unfollow failed:', err);
  } finally {
    followLoading.value = false;
  }
};

const onPortfolioSaved = () => {
  if (portfolioViewRef.value) {
    portfolioViewRef.value.refresh();
  }
};

const onPostDeleted = (post: any) => {
  emit('delete', post);
};

watch(lastDeletedPostUri, (newUri) => {
  if (newUri) {
    posts.value = posts.value.filter(p => (p.post?.uri || p.uri) !== newUri);
  }
});

const refresh = async () => {
  loadingPosts.value = true;
  try {
    await Promise.all([
      loadProfile(),
      loadUserPosts(),
      portfolioViewRef.value?.refresh()
    ]);
  } finally {
    loadingPosts.value = false;
  }
};

const renderContent = (text: string, facets?: any[]) => renderRichText(text, facets);

const handleContentClick = (event: MouseEvent) => {
  handleRichTextClick(event, { 
    router, 
    onTagClick: (tag: string) => emit('tag-click', tag) 
  });
};

const onProfileUpdated = async () => {
  await refresh();
  snackbarText.value = 'Profile updated successfully';
  snackbar.value = true;
};

defineExpose({ onPostDeleted, refresh, notesViewRef });
onMounted(() => {
  loadProfile();
  loadUserPosts();
});

watch(() => userDid.value, async (newVal, oldVal) => {
  if (newVal !== oldVal) {
    if (!isSingleNoteView.value) {
      activeTab.value = 'about';
    }
    posts.value = [];
    localProfile.value = null;
    await loadProfile();
    loadUserPosts();
  }
});

watch(activeTab, (newTab) => {
  if (newTab === 'posts' && posts.value.length === 0) {
    loadUserPosts();
  }
  if (newTab === 'jobs') {
    loadJobPosts();
  }
});
</script>

<script lang="ts">
export default {
  name: 'ProfileView'
}
</script>

<style scoped>
.profile-view-container {
  width: 100%;
}

.profile-header {
  transition: all 0.3s ease;
  border-radius: 20px 20px 0 0 !important;
}

.header-top-rounded {
  border-radius: 20px 20px 0 0 !important;
}

.bg-black-05 {
  background: rgba(0, 0, 0, 0.2) !important;
}

.border-white-05 {
  border-color: rgba(255, 255, 255, 0.05) !important;
}

.profile-banner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.7));
  z-index: 0;
}

.gap-4 {
  gap: 16px;
}

.hover-opacity:hover {
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.cursor-pointer {
  cursor: pointer;
}

.border-2 {
  border-width: 2px !important;
  border-style: solid;
}

.border-dashed {
  border-style: dashed !important;
}
.profile-tab-wrapper {
  overflow: hidden;
  position: relative;
  width: 100%;
}

:deep(.overflow-visible),
:deep(.overflow-visible .v-window__container) {
  overflow: visible !important;
}
</style>
